import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useI18n } from '../i18n/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  LayoutDashboard, Users, KanbanSquare, MailPlus, FileText, Upload, Search, Plus,
  Trash2, BellRing, Send, Eye, X, Ban,
} from 'lucide-react';

const spring = { type: 'spring', stiffness: 300, damping: 20 };
const inputCls = 'h-12 rounded-2xl bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40 font-bold';
const textareaCls = 'rounded-2xl bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40';
const STAGE_COLORS = {
  a_contacter: 'bg-nuit-600 text-creme',
  envoye: 'bg-sky-400/20 text-sky-300',
  relance: 'bg-amber-400/20 text-amber-300',
  discussion: 'bg-violet-400/20 text-violet-300',
  gagne: 'bg-green-400/20 text-green-300',
  sans_suite: 'bg-rose-400/20 text-rose-300',
};

const displayName = (c) => (c.prenom && c.prenom !== c.nom ? `${c.prenom} ${c.nom}` : (c.nom || c.prenom || ''));
const displayOrg = (c) => (c.organisation && c.organisation !== c.nom && c.organisation !== displayName(c) ? c.organisation : '');

export default function CrmHome() {
  const { t } = useI18n();
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState({ categories: [], phases: [], priorites: [], stages: [] });
  const [relances, setRelances] = useState([]);
  const [contacts, setContacts] = useState(null);
  const [contactTotal, setContactTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({ categorie: '', stage: '', priorite: '', search: '' });
  const [dmOpen, setDmOpen] = useState(false);
  const [dmForm, setDmForm] = useState({ email: '', subject: '', body: '', lang: 'fr' });
  const [dmSending, setDmSending] = useState(false);
  const [dmSug, setDmSug] = useState([]);
  const [kanban, setKanban] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [contactForm, setContactForm] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [editTpl, setEditTpl] = useState(null);
  const [tplForm, setTplForm] = useState({});
  const [newCamp, setNewCamp] = useState(false);
  const [campForm, setCampForm] = useState({ name: '', template_id: '', categories: [], phases: [], priorites: [], lang: 'fr' });
  const [campDetail, setCampDetail] = useState(null);
  const [confirmLaunch, setConfirmLaunch] = useState(false);
  const [confirmCancelCamp, setConfirmCancelCamp] = useState(false);
  const [confirmDeleteCamp, setConfirmDeleteCamp] = useState(false);
  const [importing, setImporting] = useState(false);

  const loadStats = () => {
    api.get('/admin/crm/stats').then((r) => setStats(r.data)).catch(() => {});
    api.get('/admin/crm/relances').then((r) => setRelances(r.data.relances)).catch(() => {});
  };
  const loadMeta = () => api.get('/admin/crm/categories').then((r) => setMeta(r.data)).catch(() => {});
  const loadContacts = (f = filters, p = page) => {
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && params.set(k, v));
    params.set('page', String(p));
    params.set('limit', '50');
    api.get(`/admin/crm/contacts?${params}`).then((r) => {
      setContacts(r.data.contacts);
      setContactTotal(r.data.total);
      setPages(r.data.pages);
    }).catch(() => setContacts([]));
  };
  const loadKanban = () => {
    Promise.all(meta.stages.map((s) =>
      api.get(`/admin/crm/contacts?stage=${s}&limit=500`).then((r) => [s, r.data.contacts]).catch(() => [s, []])
    )).then((entries) => setKanban(Object.fromEntries(entries)));
  };
  const loadCampaigns = () => api.get('/admin/crm/campaigns').then((r) => setCampaigns(r.data.campaigns)).catch(() => {});
  const loadTemplates = () => api.get('/admin/crm/templates').then((r) => setTemplates(r.data.templates)).catch(() => {});

  useEffect(() => { loadStats(); loadMeta(); loadCampaigns(); loadTemplates(); }, []);
  useEffect(() => { if (tab === 'contacts') loadContacts(); }, [tab, page]);
  useEffect(() => { if (tab === 'pipeline' && meta.stages.length) loadKanban(); }, [tab, meta.stages.length]);

  const applyFilters = () => { setPage(1); loadContacts(filters, 1); };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/crm/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(t('crm.importDone', data));
      loadStats(); loadMeta();
      if (tab === 'contacts') loadContacts();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
    finally { setImporting(false); e.target.value = ''; }
  };

  const openDetail = async (c) => {
    try {
      const { data } = await api.get(`/admin/crm/contacts/${c.contact_id}`);
      setDetail(data);
    } catch { toast.error(t('common.error')); }
  };

  const changeStage = async (cid, stage) => {
    try {
      await api.post(`/admin/crm/contacts/${cid}/stage`, { stage });
      toast.success(t('crm.saved'));
      if (detail?.contact?.contact_id === cid) openDetail({ contact_id: cid });
      loadStats();
      if (tab === 'pipeline') loadKanban();
      if (tab === 'contacts') loadContacts();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const addNote = async () => {
    if (!noteText.trim() || !detail) return;
    try {
      await api.post(`/admin/crm/contacts/${detail.contact.contact_id}/notes`, { text: noteText });
      setNoteText('');
      openDetail(detail.contact);
    } catch { toast.error(t('common.error')); }
  };

  const saveContact = async () => {
    try {
      if (editing === 'new') await api.post('/admin/crm/contacts', contactForm);
      else await api.put(`/admin/crm/contacts/${editing}`, contactForm);
      toast.success(t('crm.saved'));
      setEditing(null);
      loadContacts(); loadStats();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/crm/contacts/${deleteTarget}`);
      toast.success(t('crm.deleted'));
      setDetail(null);
      loadContacts(); loadStats();
      if (tab === 'pipeline') loadKanban();
    } catch { toast.error(t('common.error')); }
    setDeleteTarget(null);
  };

  const saveTemplate = async () => {
    try {
      await api.put(`/admin/crm/templates/${editTpl}`, tplForm);
      toast.success(t('crm.savedTemplate'));
      setEditTpl(null);
      loadTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const createCampaign = async () => {
    try {
      await api.post('/admin/crm/campaigns', campForm);
      toast.success(t('crm.campaignCreated'));
      setNewCamp(false);
      setCampForm({ name: '', template_id: '', categories: [], phases: [], priorites: [], lang: 'fr' });
      loadCampaigns();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const openCampaign = async (c) => {
    try {
      const { data } = await api.get(`/admin/crm/campaigns/${c.campaign_id}`);
      setCampDetail(data);
    } catch { toast.error(t('common.error')); }
  };

  const generate = async () => {
    try {
      const { data } = await api.post(`/admin/crm/campaigns/${campDetail.campaign.campaign_id}/generate`);
      toast.success(t('crm.generated', { n: data.created }));
      openCampaign(campDetail.campaign);
      loadCampaigns();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const launch = async () => {
    try {
      const { data } = await api.post(`/admin/crm/campaigns/${campDetail.campaign.campaign_id}/launch`);
      toast.success(t('crm.launched'));
      setConfirmLaunch(false);
      openCampaign(campDetail.campaign);
      loadCampaigns(); loadStats();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const cancelCampaign = async () => {
    try {
      await api.post(`/admin/crm/campaigns/${campDetail.campaign.campaign_id}/cancel`);
      toast.success(t('crm.statusCancelled'));
      setConfirmCancelCamp(false);
      openCampaign(campDetail.campaign);
      loadCampaigns();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const deleteCampaign = async () => {
    try {
      await api.delete(`/admin/crm/campaigns/${campDetail.campaign.campaign_id}`);
      toast.success(t('crm.campaignDeleted'));
      setConfirmDeleteCamp(false);
      setCampDetail(null);
      loadCampaigns(); loadStats();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const searchRecipients = async (q) => {
    setDmForm((f) => ({ ...f, email: q }));
    if (q.trim().length < 2) { setDmSug([]); return; }
    try {
      const [c, u] = await Promise.all([
        api.get(`/admin/crm/contacts?search=${encodeURIComponent(q.trim())}&limit=5`),
        api.get('/admin/users'),
      ]);
      const ql = q.trim().toLowerCase();
      const contactSug = (c.data.contacts || [])
        .map((x) => ({ label: `${displayName(x)}${displayOrg(x) ? ` — ${displayOrg(x)}` : ''}`, email: x.email }));
      const userSug = (u.data.users || [])
        .filter((x) => (x.name || '').toLowerCase().includes(ql) || (x.email || '').includes(ql))
        .slice(0, 5)
        .map((x) => ({ label: `${x.name} (${x.role})`, email: x.email }));
      const seen = new Set();
      setDmSug([...userSug, ...contactSug]
        .filter((s) => s.email && !seen.has(s.email) && seen.add(s.email)).slice(0, 6));
    } catch { setDmSug([]); }
  };

  const sendDm = async () => {
    if (!dmForm.email.includes('@') || !dmForm.subject.trim() || !dmForm.body.trim()) {
      toast.error(t('crm.dmInvalid'));
      return;
    }
    setDmSending(true);
    try {
      await api.post('/admin/crm/direct-message', dmForm);
      toast.success(t('crm.dmSent'));
      setDmOpen(false);
      setDmForm({ email: '', subject: '', body: '', lang: 'fr' });
      setDmSug([]);
    } catch (err) { const d = err.response?.data?.detail; toast.error(typeof d === 'string' ? d : t('common.error')); }
    finally { setDmSending(false); }
  };

  const stageBadge = (s) => (
    <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${STAGE_COLORS[s] || 'bg-nuit-600 text-creme'}`}>
      {t(`crm.stage.${s}`, undefined, s)}
    </span>
  );

  const campStatus = (s) => t(`crm.status${s.charAt(0).toUpperCase() + s.slice(1)}`, undefined, s);

  const statCards = stats ? [
    { icon: Users, label: t('crm.kpiContacts'), value: stats.contacts_total, testid: 'crm-kpi-contacts' },
    { icon: Send, label: t('crm.kpiSent'), value: stats.emails_sent, testid: 'crm-kpi-sent' },
    { icon: LayoutDashboard, label: t('crm.kpiEngaged'), value: `${stats.engagement_rate}%`, testid: 'crm-kpi-engaged' },
    { icon: BellRing, label: t('crm.kpiRelances'), value: stats.relances_due, testid: 'crm-kpi-relances' },
    { icon: Ban, label: t('crm.unsubscribedBadge'), value: stats.contacts_unsubscribed, testid: 'crm-kpi-unsub' },
  ] : [];

  return (
    <div data-testid="crm-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={spring}>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-creme">{t('crm.title')}</h1>
        <p className="text-base sm:text-lg text-brume mt-3">{t('crm.subtitle')}</p>
      </motion.div>

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 rounded-3xl bg-nuit-800 border border-nuit-700 p-2 h-auto">
          <TabsTrigger value="dashboard" data-testid="crm-tab-dashboard" className="flex flex-col items-center justify-center gap-1.5 min-h-[64px] rounded-2xl px-3 py-3 font-bold text-sm text-brume transition-colors hover:bg-nuit-700 data-[state=active]:bg-or-400 data-[state=active]:text-nuit-950 max-sm:last:col-span-2"><LayoutDashboard className="w-5 h-5" /> {t('crm.tabDashboard')}</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="crm-tab-contacts" className="flex flex-col items-center justify-center gap-1.5 min-h-[64px] rounded-2xl px-3 py-3 font-bold text-sm text-brume transition-colors hover:bg-nuit-700 data-[state=active]:bg-or-400 data-[state=active]:text-nuit-950 max-sm:last:col-span-2"><Users className="w-5 h-5" /> {t('crm.tabContacts')}</TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="crm-tab-pipeline" className="flex flex-col items-center justify-center gap-1.5 min-h-[64px] rounded-2xl px-3 py-3 font-bold text-sm text-brume transition-colors hover:bg-nuit-700 data-[state=active]:bg-or-400 data-[state=active]:text-nuit-950 max-sm:last:col-span-2"><KanbanSquare className="w-5 h-5" /> {t('crm.tabPipeline')}</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="crm-tab-campaigns" className="flex flex-col items-center justify-center gap-1.5 min-h-[64px] rounded-2xl px-3 py-3 font-bold text-sm text-brume transition-colors hover:bg-nuit-700 data-[state=active]:bg-or-400 data-[state=active]:text-nuit-950 max-sm:last:col-span-2"><MailPlus className="w-5 h-5" /> {t('crm.tabCampaigns')}</TabsTrigger>
          <TabsTrigger value="templates" data-testid="crm-tab-templates" className="flex flex-col items-center justify-center gap-1.5 min-h-[64px] rounded-2xl px-3 py-3 font-bold text-sm text-brume transition-colors hover:bg-nuit-700 data-[state=active]:bg-or-400 data-[state=active]:text-nuit-950 max-sm:last:col-span-2"><FileText className="w-5 h-5" /> {t('crm.tabTemplates')}</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-8">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {statCards.map((s, i) => (
              <motion.div key={s.testid} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ ...spring, delay: i * 0.05 }}
                className="bg-nuit-800 border border-nuit-700 rounded-3xl p-5" data-testid={s.testid}>
                <s.icon className="w-5 h-5 text-or-400 mb-2" strokeWidth={2.5} />
                <p className="font-fredoka font-semibold text-3xl text-creme">{s.value}</p>
                <p className="text-xs font-bold text-brume mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mt-6">
            <div className="bg-nuit-800 border border-nuit-700 rounded-3xl p-6" data-testid="crm-funnel">
              <h2 className="text-xl font-semibold text-creme mb-4">{t('crm.funnelTitle')}</h2>
              {meta.stages.map((s) => {
                const n = stats?.by_stage?.[s] || 0;
                const pct = stats?.contacts_total ? Math.round(n / stats.contacts_total * 100) : 0;
                return (
                  <div key={s} className="mb-3" data-testid={`crm-funnel-${s}`}>
                    <div className="flex justify-between text-sm font-bold mb-1">
                      <span className="text-creme">{t(`crm.stage.${s}`, undefined, s)}</span>
                      <span className="text-brume">{n} ({pct}%)</span>
                    </div>
                    <div className="h-3 rounded-full bg-nuit-700 overflow-hidden">
                      <div className="h-full bg-or-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              <p className="text-xs text-brume mt-4">
                {t('crm.contactRate')} : {stats?.contact_rate ?? 0}% — {t('crm.engagementRate')} : {stats?.engagement_rate ?? 0}%
              </p>
            </div>
            <div className="bg-nuit-800 border border-nuit-700 rounded-3xl p-6" data-testid="crm-by-category">
              <h2 className="text-xl font-semibold text-creme mb-4">{t('crm.byCategory')}</h2>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {(stats?.by_category || []).map((c) => (
                  <div key={c.categorie} className="flex justify-between text-sm">
                    <span className="text-creme font-semibold">{c.categorie}</span>
                    <span className="text-or-300 font-bold">{c.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-nuit-800 border border-nuit-700 rounded-3xl p-6 mt-6" data-testid="crm-relances">
            <h2 className="text-xl font-semibold text-creme mb-4 flex items-center gap-2">
              <BellRing className="w-5 h-5 text-or-400" /> {t('crm.relancesTitle')} ({relances.length})
            </h2>
            <div className="space-y-2">
              {relances.slice(0, 20).map((r) => (
                <button key={r.contact_id} onClick={() => { setTab('contacts'); openDetail(r); }}
                  data-testid={`crm-relance-${r.contact_id}`}
                  className="w-full flex items-center justify-between gap-3 bg-nuit-900 rounded-2xl px-4 py-3 hover:bg-nuit-700 transition-colors text-left">
                  <div>
                    <p className="font-bold text-creme text-sm">{[displayName(r), displayOrg(r)].filter(Boolean).join(' — ')}</p>
                    <p className="text-xs text-brume">{r.categorie}</p>
                  </div>
                  <span className="text-xs font-bold text-amber-300 shrink-0">
                    {t('crm.daysAgo', { n: r.days_since_send, d: r.relance_days })}
                  </span>
                </button>
              ))}
              {relances.length === 0 && <p className="text-brume font-semibold" data-testid="crm-relances-empty">{t('crm.relancesEmpty')}</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="contacts" className="mt-8">
          <div className="flex flex-wrap items-end gap-3 mb-6">
            <div className="relative">
              <Search className="w-4 h-4 text-brume absolute left-4 top-1/2 -translate-y-1/2" />
              <Input data-testid="crm-search" value={filters.search} placeholder={t('crm.searchPlaceholder')}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                className={`${inputCls} pl-10 w-64`} />
            </div>
            <Select value={filters.categorie || 'all'} onValueChange={(v) => setFilters({ ...filters, categorie: v === 'all' ? '' : v })}>
              <SelectTrigger data-testid="crm-filter-categorie" className="w-52 h-12 rounded-2xl bg-nuit-800 border-nuit-600 text-creme font-bold"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-nuit-800 border-nuit-600 rounded-2xl">
                <SelectItem value="all" className="text-creme">{t('crm.allCategories')}</SelectItem>
                {meta.categories.map((c) => <SelectItem key={c} value={c} className="text-creme">{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.stage || 'all'} onValueChange={(v) => setFilters({ ...filters, stage: v === 'all' ? '' : v })}>
              <SelectTrigger data-testid="crm-filter-stage" className="w-44 h-12 rounded-2xl bg-nuit-800 border-nuit-600 text-creme font-bold"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-nuit-800 border-nuit-600 rounded-2xl">
                <SelectItem value="all" className="text-creme">{t('crm.allStages')}</SelectItem>
                {meta.stages.map((s) => <SelectItem key={s} value={s} className="text-creme">{t(`crm.stage.${s}`, undefined, s)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={applyFilters} data-testid="crm-apply-filters" className="h-12 rounded-full bg-nuit-700 hover:bg-nuit-600 text-creme font-bold px-6">OK</Button>
            <label className={`h-12 rounded-full border-2 border-or-400/60 text-or-300 font-bold px-6 inline-flex items-center gap-2 cursor-pointer hover:bg-or-400/10 transition-all ${importing ? 'opacity-50' : ''}`}
              data-testid="crm-import-button">
              <Upload className="w-4 h-4" /> {importing ? t('common.loading') : t('crm.importBtn')}
              <input type="file" accept=".xlsx" className="hidden" onChange={importFile} disabled={importing} data-testid="crm-import-input" />
            </label>
            <Button onClick={() => setDmOpen(true)} data-testid="crm-dm-open" variant="ghost"
              className="rounded-full font-bold text-or-300 border-2 border-or-400/40 hover:bg-or-400/10 gap-2">
              <MailPlus className="w-4 h-4" /> {t('crm.dmOpen')}
            </Button>
            <Button onClick={() => { setContactForm({}); setEditing('new'); }} data-testid="crm-new-contact"
              className="h-12 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold gap-2">
              <Plus className="w-4 h-4" /> {t('crm.newContact')}
            </Button>
          </div>

          <div className="bg-nuit-800 border border-nuit-700 rounded-3xl overflow-x-auto">
            <table className="w-full text-sm" data-testid="crm-contacts-table">
              <thead>
                <tr className="border-b border-nuit-700">
                  <th className="p-4 font-bold text-brume text-left">{t('crm.colContact')}</th>
                  <th className="p-4 font-bold text-brume text-left">{t('crm.colCategory')}</th>
                  <th className="p-4 font-bold text-brume text-left">{t('crm.colEmail')}</th>
                  <th className="p-4 font-bold text-brume text-left">{t('crm.colStage')}</th>
                </tr>
              </thead>
              <tbody>
                {(contacts || []).map((c) => (
                  <tr key={c.contact_id} onClick={() => openDetail(c)}
                    className="border-b border-nuit-700 hover:bg-nuit-700/40 cursor-pointer"
                    data-testid={`crm-contact-row-${c.contact_id}`}>
                    <td className="p-4">
                      <p className="font-bold text-creme">{displayName(c)}</p>
                      <p className="text-xs text-brume">{[displayOrg(c), c.fonction].filter(Boolean).join(' — ')}</p>
                    </td>
                    <td className="p-4"><span className="bg-nuit-900 text-brume px-3 py-1 rounded-full text-xs font-bold">{c.categorie}</span></td>
                    <td className="p-4 text-brume">{c.email}</td>
                    <td className="p-4">
                      <span className="inline-flex items-center gap-2">
                        {stageBadge(c.stage)}
                        {c.unsubscribed && <span className="text-xs font-bold text-rose-400">{t('crm.unsubscribedBadge')}</span>}
                      </span>
                    </td>
                  </tr>
                ))}
                {contacts && contacts.length === 0 && (
                  <tr><td colSpan="4" className="p-8 text-center text-brume font-semibold" data-testid="crm-contacts-empty">{t('educator.emptyAll')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm font-bold text-brume" data-testid="crm-contacts-total">{contactTotal} {t('crm.kpiContacts').toLowerCase()}</p>
            <div className="flex gap-2">
              <Button variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="crm-page-prev"
                className="rounded-full text-or-300 font-bold">←</Button>
              <span className="text-sm font-bold text-brume self-center">{t('crm.page', { page, pages })}</span>
              <Button variant="ghost" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="crm-page-next"
                className="rounded-full text-or-300 font-bold">→</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pipeline" className="mt-8">
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="crm-kanban">
            {meta.stages.map((s) => (
              <div key={s} className="bg-nuit-800/60 border border-nuit-700 rounded-3xl p-3 min-h-[200px]" data-testid={`crm-kanban-${s}`}>
                <p className="font-bold text-creme text-sm mb-3 px-2 flex items-center justify-between">
                  {t(`crm.stage.${s}`, undefined, s)}
                  <span className="text-or-300">{kanban?.[s]?.length ?? '…'}</span>
                </p>
                <div className="space-y-2">
                  {(kanban?.[s] || []).map((c) => (
                    <button key={c.contact_id} onClick={() => openDetail(c)} data-testid={`crm-kanban-card-${c.contact_id}`}
                      className="w-full text-left bg-nuit-900 border border-nuit-600 rounded-2xl p-3 hover:border-or-400/50 transition-all">
                      <p className="font-bold text-creme text-xs">{displayName(c)}</p>
                      <p className="text-[11px] text-brume truncate">{displayOrg(c)}</p>
                      <p className="text-[10px] text-or-400/80 font-bold mt-1">{c.categorie}</p>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="campaigns" className="mt-8">
          <div className="flex justify-end mb-6">
            <Button onClick={() => setNewCamp(true)} data-testid="crm-new-campaign"
              className="rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold gap-2">
              <Plus className="w-4 h-4" /> {t('crm.newCampaign')}
            </Button>
          </div>
          <div className="space-y-4" data-testid="crm-campaigns-list">
            {campaigns.map((c) => (
              <button key={c.campaign_id} onClick={() => openCampaign(c)} data-testid={`crm-campaign-${c.campaign_id}`}
                className="w-full text-left bg-nuit-800 border border-nuit-700 rounded-3xl p-6 hover:border-or-400/40 transition-all">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="font-bold text-creme text-lg">{c.name}</p>
                    <p className="text-sm text-brume">{c.template_name} — {new Date(c.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-brume">
                      {t('crm.msgSent')} : {c.stats?.sent || 0} / {c.stats?.total || 0}
                    </span>
                    <span className={`px-3 py-1.5 rounded-full text-xs font-bold
                      ${c.status === 'done' ? 'bg-green-400/20 text-green-300' : c.status === 'sending' ? 'bg-amber-400/20 text-amber-300' : c.status === 'cancelled' ? 'bg-rose-400/20 text-rose-300' : 'bg-nuit-600 text-creme'}`}
                      data-testid={`crm-campaign-status-${c.campaign_id}`}>
                      {campStatus(c.status)}
                    </span>
                  </div>
                </div>
              </button>
            ))}
            {campaigns.length === 0 && <p className="text-brume font-semibold text-center py-8" data-testid="crm-campaigns-empty">{t('admin.mcEmpty')}</p>}
          </div>
        </TabsContent>

        <TabsContent value="templates" className="mt-8">
          <p className="text-sm text-brume mb-6">{t('crm.templatesIntro')}</p>
          <div className="grid md:grid-cols-2 gap-4" data-testid="crm-templates-list">
            {templates.map((tp) => (
              <div key={tp.template_id} className="bg-nuit-800 border border-nuit-700 rounded-3xl p-6" data-testid={`crm-template-${tp.template_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-bold text-creme">{tp.template_id} — {tp.name}</p>
                    <p className="text-xs text-brume mt-1">{tp.target} · J+{tp.relance_days}</p>
                  </div>
                  <Button variant="ghost" onClick={() => { setTplForm({ ...tp }); setEditTpl(tp.template_id); }}
                    data-testid={`crm-template-edit-${tp.template_id}`}
                    className="rounded-full text-or-300 hover:bg-nuit-700 font-bold shrink-0">
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-sm text-or-300 font-semibold mt-3 line-clamp-1">{tp.subject_fr}</p>
                <p className="text-xs text-brume mt-2 line-clamp-3 whitespace-pre-line">{tp.body_fr}</p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Détail contact */}
      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-nuit-800 border-nuit-700" data-testid="crm-contact-dialog">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-2xl text-creme">
              {detail?.contact?.prenom} {detail?.contact?.nom}
            </DialogTitle>
            <DialogDescription className="sr-only">{t('crm.detailTitle')}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                {stageBadge(detail.contact.stage)}
                {detail.contact.unsubscribed === true && <span className="text-xs font-bold text-rose-400">{t('crm.unsubscribedBadge')}</span>}
                <span className="text-xs font-bold text-brume bg-nuit-900 px-3 py-1.5 rounded-full">{detail.contact.categorie}</span>
                {detail.contact.priorite && <span className="text-xs font-bold text-or-300 bg-or-400/15 px-3 py-1.5 rounded-full">{detail.contact.priorite}</span>}
              </div>
              <div className="grid sm:grid-cols-2 gap-2 text-sm">
                {[['Email', detail.contact.email], [t('account.role'), detail.contact.fonction],
                  ['Organisation', detail.contact.organisation], ['Ville / Pays', [detail.contact.ville, detail.contact.pays].filter(Boolean).join(' / ')],
                  ['Téléphone', detail.contact.telephone], ['Site', detail.contact.site],
                  ['Phase', detail.contact.phase], ['Source', detail.contact.source],
                ].filter(([, v]) => v).map(([l, v]) => (
                  <p key={l} className="text-brume"><span className="font-bold text-creme">{l} :</span> {v}</p>
                ))}
              </div>
              {detail.contact.last_sent_at && (
                <p className="text-xs text-brume">{t('crm.lastSent')} : {new Date(detail.contact.last_sent_at).toLocaleString()}</p>
              )}
              <div>
                <label className="block text-sm font-bold text-creme mb-2">{t('crm.moveTo')}</label>
                <div className="flex flex-wrap gap-2">
                  {meta.stages.filter((s) => s !== detail.contact.stage).map((s) => (
                    <button key={s} onClick={() => changeStage(detail.contact.contact_id, s)}
                      data-testid={`crm-move-${s}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border border-nuit-600 hover:border-or-400/60 transition-all ${STAGE_COLORS[s]}`}>
                      {t(`crm.stage.${s}`, undefined, s)}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-creme mb-2">{t('crm.notes')}</label>
                <div className="space-y-2 max-h-48 overflow-y-auto mb-3" data-testid="crm-interactions">
                  {detail.interactions.map((i) => (
                    <div key={i.interaction_id} className="bg-nuit-900 rounded-2xl px-4 py-2.5">
                      <p className="text-xs font-bold text-or-400 uppercase">{i.type} — {new Date(i.created_at).toLocaleString()}</p>
                      <p className="text-sm text-creme mt-0.5">{i.content}</p>
                    </div>
                  ))}
                  {detail.interactions.length === 0 && <p className="text-xs text-brume">—</p>}
                </div>
                <div className="flex gap-2">
                  <Input data-testid="crm-note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)}
                    placeholder={t('crm.notePlaceholder')} className={inputCls} />
                  <Button onClick={addNote} data-testid="crm-note-add" className="rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold shrink-0">+</Button>
                </div>
              </div>
              <div className="flex justify-between">
                <Button variant="ghost" onClick={() => { setContactForm({ ...detail.contact }); setEditing(detail.contact.contact_id); }}
                  data-testid="crm-contact-edit" className="rounded-full text-or-300 font-bold hover:bg-nuit-700">{t('educator.edit')}</Button>
                <Button variant="ghost" onClick={() => setDeleteTarget(detail.contact.contact_id)}
                  data-testid="crm-contact-delete" className="rounded-full text-rose-400 font-bold hover:bg-rose-950">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Nouveau / édition contact */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl bg-nuit-800 border-nuit-700" data-testid="crm-contact-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-2xl text-creme">{t('crm.newContact')}</DialogTitle>
            <DialogDescription className="sr-only">{t('crm.newContact')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {[['prenom', t('registerYouth.name')], ['nom', t('crm.colContact')], ['email', t('crm.colEmail') + ' *'],
              ['organisation', 'Organisation'], ['fonction', t('account.role')], ['telephone', 'Téléphone'],
              ['ville', 'Ville'], ['pays', 'Pays'], ['site', 'Site / Canal']].map(([k, label]) => (
              <Input key={k} data-testid={`crm-form-${k}`} value={contactForm[k] || ''} placeholder={label}
                onChange={(e) => setContactForm({ ...contactForm, [k]: e.target.value })} className={inputCls} />
            ))}
            <Select value={contactForm.categorie || meta.categories[0] || 'Autre'} onValueChange={(v) => setContactForm({ ...contactForm, categorie: v })}>
              <SelectTrigger data-testid="crm-form-categorie" className="w-full h-12 rounded-2xl bg-nuit-900 border-nuit-600 text-creme font-bold"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-nuit-800 border-nuit-600 rounded-2xl">
                {[...meta.categories, 'Autre'].map((c) => <SelectItem key={c} value={c} className="text-creme">{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={contactForm.priorite || 'Moyenne'} onValueChange={(v) => setContactForm({ ...contactForm, priorite: v })}>
              <SelectTrigger data-testid="crm-form-priorite" className="w-full h-12 rounded-2xl bg-nuit-900 border-nuit-600 text-creme font-bold"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-nuit-800 border-nuit-600 rounded-2xl">
                {['Haute', 'Moyenne', 'Basse'].map((p) => <SelectItem key={p} value={p} className="text-creme">{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={saveContact} data-testid="crm-contact-save" disabled={!contactForm.email?.includes('@')}
              className="w-full h-12 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold">{t('educator.save')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Nouvelle campagne */}
      <Dialog open={newCamp} onOpenChange={setNewCamp}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl bg-nuit-800 border-nuit-700" data-testid="crm-campaign-dialog">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-2xl text-creme">{t('crm.newCampaign')}</DialogTitle>
            <DialogDescription className="sr-only">{t('crm.newCampaign')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('crm.campaignName')}</label>
              <Input data-testid="crm-campaign-name" value={campForm.name} placeholder="ex : Presse Phase 1 — septembre"
                onChange={(e) => setCampForm({ ...campForm, name: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('crm.campaignTemplate')}</label>
              <Select value={campForm.template_id} onValueChange={(v) => setCampForm({ ...campForm, template_id: v })}>
                <SelectTrigger data-testid="crm-campaign-template" className="w-full h-12 rounded-2xl bg-nuit-900 border-nuit-600 text-creme font-bold"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-nuit-800 border-nuit-600 rounded-2xl">
                  {templates.map((tp) => <SelectItem key={tp.template_id} value={tp.template_id} className="text-creme">{tp.template_id} — {tp.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('crm.campaignSegment')}</label>
              <div className="flex flex-wrap gap-2" data-testid="crm-campaign-categories">
                {meta.categories.map((c) => (
                  <button key={c} type="button"
                    onClick={() => setCampForm((f) => ({ ...f, categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c] }))}
                    data-testid={`crm-campaign-cat-${c}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all
                      ${campForm.categories.includes(c) ? 'bg-or-400 text-nuit-950 border-or-400' : 'bg-nuit-900 text-brume border-nuit-600'}`}>
                    {c}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {['Phase 1', 'Phase 2', 'Phase 3'].map((p) => (
                  <button key={p} type="button"
                    onClick={() => setCampForm((f) => ({ ...f, phases: f.phases.includes(p) ? f.phases.filter((x) => x !== p) : [...f.phases, p] }))}
                    data-testid={`crm-campaign-phase-${p}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all
                      ${campForm.phases.includes(p) ? 'bg-or-400 text-nuit-950 border-or-400' : 'bg-nuit-900 text-brume border-nuit-600'}`}>
                    {p}
                  </button>
                ))}
                {['Haute', 'Moyenne', 'Basse'].map((p) => (
                  <button key={p} type="button"
                    onClick={() => setCampForm((f) => ({ ...f, priorites: f.priorites.includes(p) ? f.priorites.filter((x) => x !== p) : [...f.priorites, p] }))}
                    data-testid={`crm-campaign-prio-${p}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all
                      ${campForm.priorites.includes(p) ? 'bg-or-400 text-nuit-950 border-or-400' : 'bg-nuit-900 text-brume border-nuit-600'}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('crm.campaignLang')}</label>
              <Select value={campForm.lang} onValueChange={(v) => setCampForm({ ...campForm, lang: v })}>
                <SelectTrigger data-testid="crm-campaign-lang" className="w-40 h-12 rounded-2xl bg-nuit-900 border-nuit-600 text-creme font-bold"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-nuit-800 border-nuit-600 rounded-2xl">
                  <SelectItem value="fr" className="text-creme">Français</SelectItem>
                  <SelectItem value="en" className="text-creme">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={createCampaign} data-testid="crm-campaign-create" disabled={!campForm.name.trim() || !campForm.template_id}
              className="w-full h-12 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold">{t('crm.create')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Détail campagne */}
      <Dialog open={!!campDetail} onOpenChange={(open) => !open && setCampDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl bg-nuit-800 border-nuit-700" data-testid="crm-campaign-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-2xl text-creme">{campDetail?.campaign?.name}</DialogTitle>
            <DialogDescription className="sr-only">{t('crm.tabCampaigns')}</DialogDescription>
          </DialogHeader>
          {campDetail && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-bold text-brume">{campDetail.template?.name} · {campDetail.campaign.lang?.toUpperCase()}</span>
                <span className="text-sm font-bold text-or-300" data-testid="crm-campaign-stats">
                  {t('crm.msgSent')} {campDetail.stats.sent || 0} · {t('crm.msgPending')} {campDetail.stats.pending || 0} · {t('crm.msgFailed')} {campDetail.stats.failed || 0}
                </span>
              </div>
              <div className="flex flex-wrap gap-3">
                {['draft', 'generated'].includes(campDetail.campaign.status) && (
                  <Button onClick={generate} data-testid="crm-campaign-generate"
                    className="rounded-full bg-nuit-700 hover:bg-nuit-600 text-creme font-bold gap-2">
                    <Eye className="w-4 h-4" /> {t('crm.generate')}
                  </Button>
                )}
                {campDetail.campaign.status === 'generated' && (campDetail.stats.pending || 0) > 0 && (
                  <Button onClick={() => setConfirmLaunch(true)} data-testid="crm-campaign-launch"
                    className="rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold gap-2">
                    <Send className="w-4 h-4" /> {t('crm.launch')}
                  </Button>
                )}
                {['generated', 'sending'].includes(campDetail.campaign.status) && (
                  <Button onClick={() => setConfirmCancelCamp(true)} data-testid="crm-campaign-cancel" variant="ghost"
                    className="rounded-full text-rose-400 font-bold hover:bg-rose-950 gap-2">
                    <X className="w-4 h-4" /> {t('crm.cancelCampaign')}
                  </Button>
                )}
                {campDetail.campaign.status !== 'sending' && (
                  <Button onClick={() => setConfirmDeleteCamp(true)} data-testid="crm-campaign-delete" variant="ghost"
                    className="rounded-full text-rose-400 font-bold hover:bg-rose-950 gap-2">
                    <Trash2 className="w-4 h-4" /> {t('crm.deleteCampaign')}
                  </Button>
                )}
              </div>
              <div className="bg-nuit-900 rounded-2xl overflow-x-auto">
                <table className="w-full text-sm" data-testid="crm-messages-table">
                  <thead>
                    <tr className="border-b border-nuit-700">
                      <th className="p-3 font-bold text-brume text-left">{t('crm.colEmail')}</th>
                      <th className="p-3 font-bold text-brume text-left">{t('crm.previewSubject')}</th>
                      <th className="p-3 font-bold text-brume text-left">{t('crm.colStage')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campDetail.messages.slice(0, 100).map((m) => (
                      <tr key={m.message_id} className="border-b border-nuit-700/50" data-testid={`crm-message-${m.message_id}`}>
                        <td className="p-3 text-creme font-semibold">{m.email}</td>
                        <td className="p-3 text-brume text-xs">{m.subject}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-[11px] font-bold
                            ${m.status === 'sent' ? 'bg-green-400/20 text-green-300' : m.status === 'failed' ? 'bg-rose-400/20 text-rose-300' : 'bg-nuit-600 text-creme'}`}>
                            {t(`crm.msg${m.status.charAt(0).toUpperCase() + m.status.slice(1)}`, undefined, m.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {campDetail.messages.length === 0 && (
                      <tr><td colSpan="3" className="p-6 text-center text-brume font-semibold">—</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Édition modèle */}
      <Dialog open={!!editTpl} onOpenChange={(open) => !open && setEditTpl(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-nuit-800 border-nuit-700" data-testid="crm-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-2xl text-creme">{t('crm.editTemplate')} — {editTpl}</DialogTitle>
            <DialogDescription className="sr-only">{t('crm.editTemplate')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="block text-sm font-bold text-creme mb-1">{t('crm.subjectFr')}</label>
              <Input data-testid="crm-tpl-subject-fr" value={tplForm.subject_fr || ''} onChange={(e) => setTplForm({ ...tplForm, subject_fr: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-1">{t('crm.bodyFr')}</label>
              <Textarea data-testid="crm-tpl-body-fr" rows={10} value={tplForm.body_fr || ''} onChange={(e) => setTplForm({ ...tplForm, body_fr: e.target.value })} className={`${textareaCls} text-sm`} />
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-1">{t('crm.subjectEn')}</label>
              <Input data-testid="crm-tpl-subject-en" value={tplForm.subject_en || ''} onChange={(e) => setTplForm({ ...tplForm, subject_en: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-1">{t('crm.bodyEn')}</label>
              <Textarea data-testid="crm-tpl-body-en" rows={10} value={tplForm.body_en || ''} onChange={(e) => setTplForm({ ...tplForm, body_en: e.target.value })} className={`${textareaCls} text-sm`} />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-bold text-creme">{t('crm.relanceDays')}</label>
                <Input type="number" min="1" max="30" data-testid="crm-tpl-relance" value={tplForm.relance_days ?? 7}
                  onChange={(e) => setTplForm({ ...tplForm, relance_days: parseInt(e.target.value, 10) || 7 })}
                  className={`${inputCls} w-24`} />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={tplForm.active !== false} onCheckedChange={(v) => setTplForm({ ...tplForm, active: v })} data-testid="crm-tpl-active" />
                <span className="text-sm font-bold text-creme">{t('admin.colActive')}</span>
              </div>
            </div>
            <Button onClick={saveTemplate} data-testid="crm-tpl-save"
              className="w-full h-12 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold">{t('educator.save')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmations */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-3xl bg-nuit-800 border-nuit-700" data-testid="crm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-fredoka text-2xl text-creme">{t('crm.deleteContact')}</AlertDialogTitle>
            <AlertDialogDescription className="text-brume">{t('crm.deleteContactText')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="crm-delete-cancel" className="rounded-full bg-transparent border-2 border-nuit-600 text-brume font-bold hover:bg-nuit-700 hover:text-creme">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="crm-delete-confirm" className="rounded-full bg-rose-500 hover:bg-rose-400 text-white font-bold">{t('educator.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmLaunch} onOpenChange={setConfirmLaunch}>
        <AlertDialogContent className="rounded-3xl bg-nuit-800 border-nuit-700" data-testid="crm-launch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-fredoka text-2xl text-creme">{t('crm.launchTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="text-brume">
              {t('crm.launchText', { n: campDetail?.stats?.pending || 0 })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="crm-launch-cancel" className="rounded-full bg-transparent border-2 border-nuit-600 text-brume font-bold hover:bg-nuit-700 hover:text-creme">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={launch} data-testid="crm-launch-confirm" className="rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold">{t('crm.launch')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmCancelCamp} onOpenChange={setConfirmCancelCamp}>
        <AlertDialogContent className="rounded-3xl bg-nuit-800 border-nuit-700" data-testid="crm-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-fredoka text-2xl text-creme">{t('crm.cancelCampaign')} ?</AlertDialogTitle>
            <AlertDialogDescription className="text-brume">
              {t('crm.cancelText', { n: campDetail?.stats?.pending || 0 })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="crm-cancel-cancel" className="rounded-full bg-transparent border-2 border-nuit-600 text-brume font-bold hover:bg-nuit-700 hover:text-creme">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={cancelCampaign} data-testid="crm-cancel-confirm" className="rounded-full bg-rose-500 hover:bg-rose-400 text-white font-bold">{t('crm.cancelCampaign')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog message direct */}
      <Dialog open={dmOpen} onOpenChange={setDmOpen}>
        <DialogContent className="bg-nuit-800 border-nuit-700 rounded-3xl" data-testid="crm-dm-dialog">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-2xl text-creme">{t('crm.dmTitle')}</DialogTitle>
            <DialogDescription className="sr-only">{t('crm.dmTitle')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Input value={dmForm.email} onChange={(e) => searchRecipients(e.target.value)}
                placeholder={t('crm.dmRecipient')} data-testid="crm-dm-recipient" className={inputCls} />
              <p className="text-xs text-brume mt-1">{t('crm.dmRecipientHelp')}</p>
              {dmSug.length > 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-nuit-900 border border-nuit-600 rounded-2xl shadow-xl overflow-hidden" data-testid="crm-dm-suggestions">
                  {dmSug.map((s) => (
                    <button key={s.email} type="button" data-testid={`crm-dm-sug-${s.email}`}
                      onClick={() => { setDmForm((f) => ({ ...f, email: s.email })); setDmSug([]); }}
                      className="w-full text-left px-4 py-2.5 text-sm text-creme hover:bg-nuit-700">
                      <span className="font-bold">{s.label}</span> <span className="text-brume">— {s.email}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Input value={dmForm.subject} onChange={(e) => setDmForm({ ...dmForm, subject: e.target.value })}
              placeholder={t('crm.dmSubject')} data-testid="crm-dm-subject" className={inputCls} />
            <Textarea value={dmForm.body} onChange={(e) => setDmForm({ ...dmForm, body: e.target.value })}
              placeholder={t('crm.dmBody')} rows={6} data-testid="crm-dm-body" className={textareaCls} />
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-brume">{t('crm.dmLang')}</span>
              <Select value={dmForm.lang} onValueChange={(v) => setDmForm({ ...dmForm, lang: v })}>
                <SelectTrigger data-testid="crm-dm-lang" className="w-32 h-11 rounded-2xl bg-nuit-900 border-nuit-600 text-creme font-bold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-nuit-800 border-nuit-600">
                  <SelectItem value="fr" className="text-creme">Français</SelectItem>
                  <SelectItem value="en" className="text-creme">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={sendDm} disabled={dmSending} data-testid="crm-dm-send"
              className="w-full h-12 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold gap-2">
              <Send className="w-4 h-4" /> {dmSending ? t('landing.checking') : t('crm.dmSend')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDeleteCamp} onOpenChange={setConfirmDeleteCamp}>
        <AlertDialogContent className="rounded-3xl bg-nuit-800 border-nuit-700" data-testid="crm-delete-campaign-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-fredoka text-2xl text-creme">{t('crm.deleteCampaign')} ?</AlertDialogTitle>
            <AlertDialogDescription className="text-brume">
              {t('crm.deleteCampaignText', { n: campDetail?.stats?.total || 0 })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="crm-delete-campaign-cancel" className="rounded-full bg-transparent border-2 border-nuit-600 text-brume font-bold hover:bg-nuit-700 hover:text-creme">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={deleteCampaign} data-testid="crm-delete-campaign-confirm" className="rounded-full bg-rose-500 hover:bg-rose-400 text-white font-bold">{t('crm.deleteCampaign')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
